/**
 * GlossaryPage — FixMySEO Progress Glossary.
 * Sprint 2.8c Phase 5.
 *
 * 15 locked entries in display order.
 * Public — accessible without login (rendered before auth check in App.tsx).
 * schema.org DefinedTermSet injected into document <head> via useEffect.
 *
 * Linked from: HeadlineBand info tooltips, DrilldownRow "Learn more →",
 *              PlatformBreakdown footer, DomainDetail footer.
 *
 * Standards Programme links follow the pattern:
 *   https://github.com/cloudwifi/fixmyseo-standards/blob/main/{slug}/v1.0.md
 */

import { useEffect } from 'react';

// ---------------------------------------------------------------------------
// Glossary data
// ---------------------------------------------------------------------------

interface GlossaryEntry {
  id: string;
  term: string;
  definition: string;
  /** First sentence, used in schema.org DefinedTerm description. */
  shortDefinition: string;
  methodologyUrl?: string;
}

const STANDARDS_BASE = 'https://github.com/cloudwifi/fixmyseo-standards/blob/main';

const ENTRIES: GlossaryEntry[] = [
  {
    id: 'ai-citation-rate',
    term: 'AI citation rate',
    methodologyUrl: `${STANDARDS_BASE}/citation-rate/v1.0.md`,
    shortDefinition: 'AI citation rate measures how often your website is linked or referenced in responses from AI assistants like ChatGPT, Google AI Overviews, Claude, and Perplexity.',
    definition: `AI citation rate measures how often your website is linked or referenced in responses from AI assistants like ChatGPT, Google AI Overviews, Claude, and Perplexity. When someone asks an AI a question and the AI includes your site as a source, that counts as a citation. A higher citation rate means AI search engines consider your content authoritative enough to reference — which increasingly drives traffic as more people use AI for discovery.

FixMySEO measures this by running hundreds of relevant prompts across major AI platforms each week and recording how many responses include a link to your domain. The result is expressed as a percentage of all prompts run.`,
  },
  {
    id: 'ai-mention-share',
    term: 'AI mention share',
    methodologyUrl: `${STANDARDS_BASE}/mention-share/v1.0.md`,
    shortDefinition: 'AI mention share measures how often your brand name or domain appears in AI responses about your category — whether or not you are linked.',
    definition: `AI mention share measures how often your brand name or domain appears in AI responses about your category — whether or not you are linked. It is sometimes called Share of Model Voice. Where citation rate counts links, mention share counts visibility: if ChatGPT names you in a response about cloud networking software, that is a mention even without a link.

This matters because AI assistants often describe brands verbally before linking to them, and mentions influence the customer's journey even when they are not clickable. A higher AI mention share means your brand features prominently in the AI conversations your potential customers are having.`,
  },
  {
    id: 'somv',
    term: 'Share of Model Voice (SoMV)',
    methodologyUrl: `${STANDARDS_BASE}/mention-share/v1.0.md`,
    shortDefinition: "Share of Model Voice (SoMV) is FixMySEO's term for the percentage of AI-generated responses about your category that mention your brand, compared to all mentions of you and your competitors combined.",
    definition: `Share of Model Voice (SoMV) is FixMySEO's term for the percentage of AI-generated responses about your category that mention your brand, compared to all mentions of you and your competitors combined. If there are 100 AI responses in your category and 30 mention you, 40 mention a competitor, and 30 mention no brand, your SoMV is 43% (30 out of 70 total brand mentions).

It is analogous to Share of Voice in traditional advertising, adapted for AI search. FixMySEO computes SoMV from the same GeoSweep prompts used to measure citation rate, running them across the major AI platforms your customers use.`,
  },
  {
    id: 'geosweep',
    term: 'GeoSweep',
    shortDefinition: "GeoSweep is FixMySEO's proprietary process for measuring how AI assistants respond to questions relevant to your business.",
    definition: `GeoSweep is FixMySEO's proprietary process for measuring how AI assistants respond to questions relevant to your business. Each sweep runs a curated set of prompts — questions a real customer might ask when looking for a product or service like yours — across ChatGPT, Google AI Overviews, Claude, Perplexity, Gemini, and Mistral. The responses are analysed to extract citation rates, mention rates, and competitive positioning.

GeoSweep runs automatically with each audit and is the engine behind your AI citation rate, AI mention share, and per-platform breakdown scores.`,
  },
  {
    id: 'recognition-framing',
    term: 'Recognition framing',
    shortDefinition: "Recognition framing is FixMySEO's approach to measuring what matters most in AI search: whether AI assistants recognise your brand as an authority in your category.",
    definition: `Recognition framing is FixMySEO's approach to measuring what matters most in AI search: whether AI assistants recognise your brand as an authority in your category. Traditional SEO measured rankings — how high up a list your page appeared. AI search does not work that way. When a customer asks ChatGPT for a recommendation, there is no list; there is a response, and either your brand is in it or it is not.

Recognition framing shifts focus from "where do I rank?" to "do AI assistants know who I am, and do they say positive things about me?" FixMySEO's three-pillar framework — SEO, AEO, GEO — is built around this principle.`,
  },
  {
    id: 'off-domain-tier-1',
    term: 'Off-domain Tier 1',
    methodologyUrl: `${STANDARDS_BASE}/off-domain-tier-1/v1.0.md`,
    shortDefinition: 'Off-domain Tier 1 signals are presences your brand has established on authoritative external platforms that AI assistants frequently cite as sources.',
    definition: `Off-domain Tier 1 signals are presences your brand has established on authoritative external platforms that AI assistants frequently cite as sources. FixMySEO tracks two in its first tier: Wikipedia (the most-cited domain across AI responses globally) and YouTube (a major source for product and how-to content).

Having a presence on these platforms makes your brand significantly more likely to appear in AI responses — AI systems are trained on and draw from these sources heavily. FixMySEO checks for your brand's presence on each platform during every audit.`,
  },
  {
    id: 'off-domain-tier-2',
    term: 'Off-domain Tier 2',
    shortDefinition: 'Off-domain Tier 2 signals are presences on a broader set of authoritative platforms beyond the core Tier 1 sources.',
    definition: `Off-domain Tier 2 signals are presences on a broader set of authoritative platforms beyond the core Tier 1 sources. These include major review platforms (G2, Capterra, Trustpilot), industry publications, and podcast mentions. Tier 2 signals represent a deeper off-domain presence: while Tier 1 covers the platforms most universally cited by AI, Tier 2 covers the platforms most authoritative within your specific industry.

FixMySEO is building Tier 2 measurement for activation in a future sprint — the data points appear in your dashboard as "coming soon" until they are live.`,
  },
  {
    id: 'reach',
    term: 'Reach',
    shortDefinition: 'Reach describes the geographic scope of the audience your brand is targeting: local (a city or region), national (a single country), or global (multiple countries).',
    definition: `Reach describes the geographic scope of the audience your brand is targeting: local (a city or region), national (a single country), or global (multiple countries). Reach affects how FixMySEO interprets your scores — a local plumber and a global SaaS company are measured against different benchmarks.

It also determines which AI prompts are relevant for your GeoSweep: a local business needs AI assistants to recommend them for location-specific queries, while a global brand needs citation in broader category-level responses. You set your reach during onboarding, and it can be updated in your domain settings.`,
  },
  {
    id: 'entity-density',
    term: 'Entity density',
    shortDefinition: 'Entity density measures how clearly and completely your website communicates the core facts about your business to machines — AI systems, search engines, and structured data parsers.',
    definition: `Entity density measures how clearly and completely your website communicates the core facts about your business to machines — AI systems, search engines, and structured data parsers. An entity is a named, identifiable concept: your business name, your location, your product category, the people who work there.

High entity density means your site clearly states who you are, what you do, and where you are in formats machines can parse — through structured data (schema.org markup), consistent NAP (name, address, phone) formatting, and explicit product and service categorisation. Low entity density is one of the most common reasons AI assistants either don't mention a brand or describe it inaccurately.`,
  },
  {
    id: 'ski-ramp',
    term: 'Ski-ramp content structure',
    shortDefinition: "The ski-ramp content structure is FixMySEO's term for pages that open with a direct, specific answer to the question the page is about, then expand into supporting detail and context.",
    definition: `The ski-ramp content structure is FixMySEO's term for pages that open with a direct, specific answer to the question the page is about, then expand into supporting detail and context — like the steep drop at the top of a ski slope followed by a longer descent. This structure is optimised for AI citation because AI assistants extract answers from the first few paragraphs of a page.

A page that buries its answer in paragraph 6 is less likely to be cited than one that answers immediately and clearly. The ski-ramp pattern contrasts with traditional blog-style content that starts with background, builds context, and reaches the answer near the end.`,
  },
  {
    id: 'ai-crawler-accessibility',
    term: 'AI crawler accessibility',
    methodologyUrl: `${STANDARDS_BASE}/ai-crawler-accessibility/v0.1.md`,
    shortDefinition: 'AI crawler accessibility measures whether the AI systems that build their knowledge bases from web content can actually read your website.',
    definition: `AI crawler accessibility measures whether the AI systems that build their knowledge bases from web content can actually read your website. Many AI assistants — including Perplexity and Claude — use web crawlers to discover and index content. If your website blocks these crawlers in your robots.txt file, or uses JavaScript rendering in ways that prevent crawling, your content may be invisible to AI systems entirely.

FixMySEO checks your robots.txt configuration, your page rendering behaviour, and whether the major AI crawlers are explicitly allowed or blocked. Fixing AI crawler accessibility is often the highest-priority fix for a new audit.`,
  },
  {
    id: 'llms-txt',
    term: 'llms.txt',
    methodologyUrl: `${STANDARDS_BASE}/ai-crawler-accessibility/v0.1.md`,
    shortDefinition: 'llms.txt is an emerging web standard (analogous to robots.txt) that tells large language models (LLMs) what content on your site is appropriate to learn from and how to reference it.',
    definition: `llms.txt is an emerging web standard (analogous to robots.txt) that tells large language models (LLMs) what content on your site is appropriate to learn from and how to reference it. A site with a well-structured llms.txt file can guide AI systems toward its most authoritative pages, specify its brand name and canonical descriptions, and indicate the licence under which its content may be used in AI training.

FixMySEO checks for the presence and quality of llms.txt as part of its AI crawler accessibility audit. This is a new standard: early adoption is a competitive advantage as AI systems increasingly prioritise it.`,
  },
  {
    id: 'discussion-preview',
    term: 'Discussion preview',
    shortDefinition: 'A discussion preview is a short excerpt of community discussion — typically from Reddit, Quora, or an industry forum — that AI assistants include in responses to show "what real people are saying" about a topic or brand.',
    definition: `A discussion preview is a short excerpt of community discussion — typically from Reddit, Quora, or an industry forum — that AI assistants include in responses to show "what real people are saying" about a topic or brand. AI systems like Perplexity and Google AI Overviews often surface these alongside traditional citations.

A positive discussion preview is a signal of real-world credibility; a negative one can undermine an otherwise strong brand presence. FixMySEO monitors discussion previews as part of off-domain signal tracking, helping you understand how your brand appears in the community content AI assistants draw from.`,
  },
  {
    id: 'inline-citation',
    term: 'Inline citation',
    shortDefinition: 'An inline citation is a link embedded within the body of an AI response — typically formatted as [Source] or as a hyperlinked domain name — that points to the website the AI is drawing information from.',
    definition: `An inline citation is a link embedded within the body of an AI response — typically formatted as [Source] or as a hyperlinked domain name — that points to the website the AI is drawing information from. Inline citations are the clearest form of AI attribution: the user can see exactly where the AI got its information and click through to your site.

This is distinct from a citation at the end of a response or a mention without a link. FixMySEO's citation rate metric specifically tracks inline citations, not just mentions, because inline citations drive traffic and signal authority to both users and the AI systems that observe click behaviour.`,
  },
  {
    id: 'subscription-source',
    term: 'Subscription source',
    shortDefinition: "Subscription source is FixMySEO's tracking of where a customer's account originated — for example, whether they signed up from the landing page, from a specific campaign, or directly.",
    definition: `Subscription source is FixMySEO's tracking of where a customer's account originated — for example, whether they signed up from the landing page, from a specific campaign, or directly. This is internal account data used to understand how customers discover FixMySEO.

It does not affect your audit scores or recommendations. You may see "Subscription source" referenced in your account settings or internal reports. This entry exists in the glossary for completeness — it is not a measurement metric and has no bearing on your SEO, AEO, or GEO scores.`,
  },
];

// ---------------------------------------------------------------------------
// Schema.org DefinedTermSet structured data
// ---------------------------------------------------------------------------

function buildSchemaOrgJsonLd(): string {
  return JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'DefinedTermSet',
    name: 'FixMySEO Progress Glossary',
    description: 'Definitions for the metrics, methods, and concepts used in the FixMySEO progress report.',
    url: 'https://app.fixmyseo.io/glossary',
    hasDefinedTerm: ENTRIES.map((entry) => ({
      '@type': 'DefinedTerm',
      '@id': `https://app.fixmyseo.io/glossary#${entry.id}`,
      name: entry.term,
      description: entry.shortDefinition,
      inDefinedTermSet: 'https://app.fixmyseo.io/glossary',
    })),
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  onBack?: () => void;
}

export function GlossaryPage({ onBack }: Props) {
  // Inject schema.org DefinedTermSet into <head>
  useEffect(() => {
    const existing = document.getElementById('schema-glossary');
    if (existing) return;
    const script = document.createElement('script');
    script.id = 'schema-glossary';
    script.type = 'application/ld+json';
    script.textContent = buildSchemaOrgJsonLd();
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-glossary');
      if (el) document.head.removeChild(el);
    };
  }, []);

  // Update page title
  useEffect(() => {
    const prev = document.title;
    document.title = 'Glossary — FixMySEO';
    return () => { document.title = prev; };
  }, []);

  return (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-brand-paper">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Back nav */}
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-600 hover:text-brand-ink mb-8 inline-flex items-center gap-1 transition-colors"
          >
            ← Back to dashboard
          </button>
        )}

        {/* Header */}
        <header className="mb-10">
          <h1 className="text-3xl font-bold text-brand-ink mb-2">How we measure progress</h1>
          <p className="text-gray-600 text-base leading-relaxed">
            Definitions for every metric, method, and concept used in the FixMySEO progress
            report. All measurements follow the{' '}
            <a
              href="https://github.com/cloudwifi/fixmyseo-standards"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="FixMySEO Standards Programme — opens in new tab"
              className="text-brand-green-dark hover:text-brand-green-dark font-medium underline underline-offset-2"
            >
              FixMySEO Standards Programme
            </a>
            {' '}where a methodology link is shown.
          </p>
        </header>

        {/* Entry list */}
        <dl className="space-y-10">
          {ENTRIES.map((entry) => (
            <div
              key={entry.id}
              id={entry.id}
              className="scroll-mt-6 border-b border-gray-100 pb-10 last:border-0 last:pb-0"
            >
              <dt className="flex items-start justify-between gap-4 mb-3">
                <h2 className="text-lg font-semibold text-brand-ink">{entry.term}</h2>
                {entry.methodologyUrl && (
                  <a
                    href={entry.methodologyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${entry.term} methodology — opens in new tab`}
                    className="flex-shrink-0 text-xs text-brand-green-dark hover:text-brand-green-dark font-medium border border-brand-green-dark/30 rounded-full px-3 py-1 transition-colors"
                  >
                    Methodology ↗
                  </a>
                )}
              </dt>
              <dd className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
                {entry.definition}
              </dd>
            </div>
          ))}
        </dl>

        {/* Footer */}
        <footer className="mt-12 pt-8 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-600">
            FixMySEO Progress Glossary · All definitions are versioned under the{' '}
            <a
              href="https://github.com/cloudwifi/fixmyseo-standards"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-green-dark hover:text-brand-green-dark underline underline-offset-2"
            >
              FixMySEO Standards Programme
            </a>
            .
          </p>
        </footer>
      </div>
    </main>
  );
}
